import { Link } from 'react-router-dom';
import { CloseOutlined, MailOutlined, UserOutlined } from '@ant-design/icons';
import { Popover } from 'antd';
import { CopyClipboard } from '@/components/copy-clipboard';
import { ROUTES } from '@/constants';
import { getUserFullName } from '@/utils/utils';

const initials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

/**
 * A person assigned to the company, as a small card in the access selects
 * (antd `tagRender`). Who may open user accounts gets their details and a
 * link to the account on click; the × still removes them while editing.
 *
 * @param {Object} props - antd tag props plus:
 * @param {Object} [props.user] - { _id, firstName, lastName, email }
 * @param {boolean} props.withDetails
 * @param {(key: string) => string} props.t
 */
export default function CompanyUserChip({
  label,
  value,
  closable,
  onClose,
  user,
  withDetails,
  t,
}) {
  // Someone just picked isn't among the saved people yet, so the option's own
  // label names them (getUserFullName would say "Default FullName").
  const name =
    user?.firstName || user?.lastName
      ? getUserFullName(user.firstName, user.lastName)
      : String(label ?? '');

  // Keep the select from opening its list when the card is pressed.
  const keepSelectClosed = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const card = (
    <span
      onMouseDown={keepSelectClosed}
      className={`my-0.5 me-1 inline-flex max-w-full items-center gap-1 rounded-full border border-zinc-200 bg-white py-px pe-1.5 ps-px text-[12px] shadow-[0_1px_2px_rgba(16,24,40,0.06)] ${withDetails ? 'cursor-pointer hover:border-accent' : ''}`}
    >
      <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[rgba(var(--accentColor),0.12)] text-[9px] font-bold text-accent">
        {initials(name) || <UserOutlined />}
      </span>
      <span className="truncate font-medium text-zinc-800">{name}</span>
      {closable && (
        <CloseOutlined
          aria-label={t('remove', { ns: 'forms' })}
          className="shrink-0 text-[10px] text-zinc-400 hover:text-zinc-700"
          onMouseDown={keepSelectClosed}
          onClick={(event) => {
            event.stopPropagation();
            onClose?.(event);
          }}
        />
      )}
    </span>
  );

  if (!withDetails) return card;

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      content={
        <div className="grid min-w-[220px] gap-1.5">
          <span className="text-sm font-bold text-zinc-900">{name}</span>
          {user?.email ? (
            <div className="flex items-center gap-1">
              <a
                href={`mailto:${user.email}`}
                className="flex items-center gap-1.5 text-[13px] text-zinc-600"
              >
                <MailOutlined /> {user.email}
              </a>
              <CopyClipboard value={user.email} />
            </div>
          ) : null}
          <Link
            to={`${ROUTES.admin}/${ROUTES.users}/view/${value}`}
            className="mt-1 text-[13px] font-semibold text-accent"
          >
            {t('openProfile', { ns: 'forms' })} →
          </Link>
        </div>
      }
    >
      {card}
    </Popover>
  );
}
